import { inject, Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { UserService } from './user.service';
import { OpcoService } from './opco.service';
import { CachingHttpClient } from './caching-http-client';
import { concat, defer, mergeMap, of, timeout } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { driveDatabase } from '../database/drive.database';
import { ServiceWorkerService } from '../core/services/service-worker.service';
import { LocalStorageService } from './local-storage.service';
import { catchError } from 'rxjs/operators';
import { NewFirebaseMessagingService } from '../core/services/new-firebase-messaging.service';
import { DriverNameService } from './driver-name.service';

@Injectable({
  providedIn: 'root'
})
export class LogoutService {
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly opcoService = inject(OpcoService);
  private readonly driverNameService = inject(DriverNameService);
  private readonly cachingHttpClient = inject(CachingHttpClient);
  private readonly serviceWorkerService = inject(ServiceWorkerService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly newFirebaseMessagingService = inject(NewFirebaseMessagingService);

  logout() {
    return this.cleanupBeforeLogout().pipe(
      catchError((error) => {
        console.error('Error during logout cleanup', error);
        return this.executeLogout(); // continue with the logout process even when cleanup fails
      }),
      mergeMap(() => this.executeLogout())
    );
  }

  private cleanupBeforeLogout() {
    // Executed sequentially to prevent excess logging by first disabling the Sentry logging and unregistering service
    // workers before deleting the IndexedDB. Promises are deferred, otherwise they would all start at once.
    return concat(
      ...[
        this.newFirebaseMessagingService.unsubscribeToken().pipe(
          timeout(5000),
          catchError((error) => {
            console.error(
              'Ignoring error while unsubscribing from notifications to prevent blocking of the underlying logout flow.',
              error
            );
            return of(false);
          })
        ),
        defer(() => Sentry.close()),
        // If unregistering service workers does not finish within 1 second, likely they are not yet ready.
        this.serviceWorkerService.unregisterAllRegistrations().pipe(
          timeout(1000),
          catchError((error) => {
            console.error('Ignoring error while unregistering service workers to still delete the database.', error);
            return of(false);
          })
        ),
        defer(() => driveDatabase.delete())
      ]
    );
  }

  private executeLogout() {
    this.cleanupLocalStorageAndInMemory();
    return this.authService.logout({
      logoutParams: { returnTo: window.location.origin }
    });
  }

  private cleanupLocalStorageAndInMemory() {
    this.userService.clear();
    this.opcoService.clear();
    this.cachingHttpClient.clear();
    this.driverNameService.clear();
    this.localStorageService.clear();
    return of(true);
  }
}
